/**
 * TemplateCard - Displays a single template with actions
 */
import React from 'react';
import { FileText, Edit2, Copy, Trash2, Star } from 'lucide-react';
import { getTemplateItemCount } from '../../../../utils/familiarisationTemplateHelpers';

const TemplateCard = ({ template, onEdit, onDuplicate, onDelete, onSetDefault }) => {
  const itemCount = getTemplateItemCount(template);
  const sectionCount = template.sections?.length || 0;

  const validityText = template.validityPeriodMonths === 0
    ? 'No expiry'
    : `${template.validityPeriodMonths} months`;

  const appliesToText = template.appliesTo?.positions?.includes('all')
    ? 'All positions'
    : template.appliesTo?.positions?.map(p =>
        p.charAt(0).toUpperCase() + p.slice(1)
      ).join(', ') || 'Not specified';

  return (
    <div className={`border rounded-lg p-4 ${template.isDefault ? 'bg-cyan-50 border-cyan-200' : 'bg-white border-gray-200'}`}>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <FileText className="h-5 w-5 text-gray-400" />
            <h4 className="font-medium text-gray-900">{template.name}</h4>
            {template.isDefault && (
              <span className="px-2 py-0.5 bg-cyan-100 text-cyan-700 text-xs rounded-full flex items-center gap-1">
                <Star className="h-3 w-3" />
                Default
              </span>
            )}
            {template.isSystem && !template.isDefault && (
              <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">
                Built-in
              </span>
            )}
            {template.type === 'custom' && (
              <span className="px-2 py-0.5 bg-purple-100 text-purple-600 text-xs rounded-full">
                Custom
              </span>
            )}
          </div>

          <p className="text-sm text-gray-600 mt-1">
            {itemCount} items &bull; {sectionCount} sections &bull; Valid: {validityText}
          </p>
          <p className="text-xs text-gray-500 mt-1">
            Applies to: {appliesToText}
          </p>
        </div>

        <div className="flex items-center gap-1 ml-4">
          <button
            onClick={() => onEdit(template)}
            className="p-2 text-gray-500 hover:text-cyan-600 hover:bg-cyan-50 rounded-lg transition-colors"
            title="Edit template"
          >
            <Edit2 className="h-4 w-4" />
          </button>
          <button
            onClick={() => onDuplicate(template)}
            className="p-2 text-gray-500 hover:text-cyan-600 hover:bg-cyan-50 rounded-lg transition-colors"
            title="Duplicate template"
          >
            <Copy className="h-4 w-4" />
          </button>
          {!template.isDefault && (
            <>
              {!template.isSystem && (
                <button
                  onClick={() => onDelete(template)}
                  className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  title="Delete template"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
              <button
                onClick={() => onSetDefault(template)}
                className="p-2 text-gray-500 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                title="Set as default"
              >
                <Star className="h-4 w-4" />
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default TemplateCard;
